import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { Database, Download, Loader2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

/**
 * Super-admin-only card that streams a full DB dump to the operator's
 * browser. Two formats:
 *   • Mongo — native mongodump archive (bson + gzip)
 *   • MySQL — JSON-column translation (one MySQL table per collection,
 *             every document preserved in a JSON column). Optionally
 *             gzip-wrapped at download time.
 *
 * The backend re-validates the super_admin role and audits every
 * dispatch — this UI guard is defense-in-depth.
 */
export function DatabaseBackupCard() {
  const [busyFormat, setBusyFormat] = useState(null);   // 'mongo' | 'mysql' | null
  const [mysqlGzip, setMysqlGzip] = useState('gzip');   // 'gzip' | 'plain'

  const download = async (format) => {
    setBusyFormat(format);
    try {
      // Use axios `responseType: 'blob'` so the streamed body lands in
      // a Blob we can turn into an <a download>. axios follows the
      // baseURL configured in `api`, so nothing extra needed.
      const params = { format };
      if (format === 'mysql') params.gzip = mysqlGzip === 'gzip' ? 1 : 0;
      const res = await api.get('/settings/db-backup', {
        params, responseType: 'blob',
      });

      // Pull the filename out of Content-Disposition so we honour the
      // backend's timestamped naming.
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      const fallback = format === 'mongo'
        ? 'db-backup.mongoarchive.gz'
        : (mysqlGzip === 'gzip' ? 'db-backup.sql.gz' : 'db-backup.sql');
      const filename = match ? match[1] : fallback;

      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} dump downloaded — ${filename}`);
    } catch (e) {
      // Blob-typed errors don't have a JSON detail — try to read it.
      let detail = e?.response?.data?.detail || e?.message || 'Download failed';
      if (e?.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          const parsed = JSON.parse(txt);
          if (parsed?.detail) detail = parsed.detail;
        } catch { /* keep raw */ }
      }
      toast.error(detail);
    } finally {
      setBusyFormat(null);
    }
  };

  return (
    <Card data-testid="db-backup-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Database className="h-5 w-5 text-primary" />
              Database Backup
            </CardTitle>
            <CardDescription className="mt-1">
              Download a full logical dump of the ERP database in your
              preferred format. Access restricted to Super Admins.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Warning banner — this file contains everything, treat like a
            root password. */}
        <div className="flex gap-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-xs text-amber-200">
          <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5 text-amber-400" />
          <div>
            Backups contain <strong>every</strong> record including hashed
            credentials, payment metadata, and personal data. Store securely
            and delete after use. Every download is audited under your name.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* --- Mongo option --- */}
          <div className="rounded-lg border p-4 space-y-3">
            <div>
              <p className="font-medium text-sm">Mongo archive (recommended)</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Native mongodump output. Restore with{' '}
                <code className="text-[11px] px-1 py-0.5 rounded bg-muted">
                  mongorestore --archive=&lt;file&gt; --gzip
                </code>
              </p>
            </div>
            <Button
              onClick={() => download('mongo')}
              disabled={busyFormat !== null}
              className="w-full"
              data-testid="db-backup-mongo"
            >
              {busyFormat === 'mongo'
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <Download className="h-4 w-4 mr-2" />}
              Download Mongo dump
            </Button>
          </div>

          {/* --- MySQL option --- */}
          <div className="rounded-lg border p-4 space-y-3">
            <div>
              <p className="font-medium text-sm">MySQL SQL dump</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                JSON-column translation — one table per collection with{' '}
                <code className="text-[11px] px-1 py-0.5 rounded bg-muted">doc JSON</code>{' '}
                preserving every field. Query with <code>JSON_EXTRACT</code>.
              </p>
            </div>
            <RadioGroup
              value={mysqlGzip}
              onValueChange={setMysqlGzip}
              className="flex gap-4"
              disabled={busyFormat !== null}
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="gzip" id="mysql-gzip" data-testid="db-backup-mysql-gzip" />
                <Label htmlFor="mysql-gzip" className="text-xs cursor-pointer">.sql.gz (compressed)</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="plain" id="mysql-plain" data-testid="db-backup-mysql-plain" />
                <Label htmlFor="mysql-plain" className="text-xs cursor-pointer">.sql (plain)</Label>
              </div>
            </RadioGroup>
            <Button
              onClick={() => download('mysql')}
              disabled={busyFormat !== null}
              className="w-full"
              variant="outline"
              data-testid="db-backup-mysql"
            >
              {busyFormat === 'mysql'
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <Download className="h-4 w-4 mr-2" />}
              Download MySQL dump
            </Button>
          </div>
        </div>

        <p className="text-[11px] text-muted-foreground">
          Downloads stream directly — the pod never buffers a full copy,
          so even multi-hundred-MB dumps work reliably. Do not close this
          tab while the download is in progress.
        </p>
      </CardContent>
    </Card>
  );
}
